import { useEffect, useMemo, useRef } from 'react';
import type { ApolloError } from 'apollo-client';

import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsCreateSpaceDrawerShowing } from '@confluence/create-space/entry-points/useIsCreateSpaceDrawerShowing';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { popOnboardingConfetti } from '@confluence/onboarding-helpers/entry-points/popOnboardingConfetti';

import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from './homeOnboardingTakeoverV2OnboardingStateKeys';
import { useIsEligibleForExplorePremiumCard } from './hooks/useIsEligibleForExplorePremiumCard';
import type {
	OnboardingActionId,
	OnboardingActionState,
	OnboardingActionStates,
	OnboardingAction,
} from './cards/HTV2CardTypes';
import { ONBOARDING_ACTION_STATES, ONBOARDING_ACTION_IDS } from './cards/HTV2CardTypes';
import { useHTV2Actions } from './useHTV2Store';
import { transformLandOnboardingActions } from './cards/HTV2LandCards';
import { transformXflowOnboardingActions } from './cards/HTV2XflowCards';

function deserializeOnboardingActionStates(
	serializedValues: string | undefined,
): OnboardingActionStates {
	try {
		const values = JSON.parse(serializedValues || '{}');
		if (typeof values !== 'object') {
			return {};
		}

		// There's no control over the shape of the object that's deserialized,
		// so we need to filter out any keys and values that aren't valid.
		const isValidState = (value: any): value is OnboardingActionState =>
			ONBOARDING_ACTION_STATES.includes(value);

		return ONBOARDING_ACTION_IDS.reduce(
			(states, id) => (isValidState(values[id]) ? { ...states, [id]: values[id] } : states),
			{} as OnboardingActionStates,
		);
	} catch (e) {
		return {};
	}
}

function serializeOnboardingActionStates(onboardingActionStates: OnboardingActionStates) {
	return JSON.stringify(onboardingActionStates);
}

export function useOnboardingActions() {
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([
		...Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS),
		EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
	]);

	const { setOnboardingState } = useSetOnboardingState();
	const {
		isEligible: isPremiumCardEligible,
		loading: premiumCardLoading,
		formattedTrialDaysLeft,
		error: premiumCardError,
	} = useIsEligibleForExplorePremiumCard();

	const {
		onboardingActionStates: serializedOnboardingActionStates,
		hasSeenConfetti,
		hasUserPublished,
	} = deserializeState(onboardingStateData);

	const onboardingActionStates = useMemo(() => {
		return deserializeOnboardingActionStates(serializedOnboardingActionStates);
	}, [serializedOnboardingActionStates]);

	const [createSpaceDrawerOpen] = useIsCreateSpaceDrawerShowing();
	const { getIsLandTakeoverType } = useHTV2Actions();
	const dismiss = useMemo(
		() => (id: OnboardingActionId) => {
			setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.onboardingActionStates,
				value: serializeOnboardingActionStates({ ...onboardingActionStates, [id]: 'dismissed' }),
			});
		},
		[onboardingActionStates, setOnboardingState],
	);

	const complete = useMemo(
		() => (id: OnboardingActionId) => {
			setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.onboardingActionStates,
				value: serializeOnboardingActionStates({ ...onboardingActionStates, [id]: 'completed' }),
			});
		},
		[onboardingActionStates, setOnboardingState],
	);

	const errors = [premiumCardError, onboardingStateError].filter(Boolean) as ApolloError[];

	const actions: OnboardingAction[] = useMemo(() => {
		const isLandTakeoverType = getIsLandTakeoverType();
		if (isLandTakeoverType) {
			return transformLandOnboardingActions({
				formattedTrialDaysLeft,
				isPremiumCardEligible,
				onboardingActionStates,
			});
		}
		return transformXflowOnboardingActions({ onboardingActionStates });
	}, [
		onboardingActionStates,
		formattedTrialDaysLeft,
		isPremiumCardEligible,
		getIsLandTakeoverType,
	]);

	useEffect(() => {
		const hasntCreatedPage = actions.some(
			(action) => action.id === 'createPage' && action.state === 'pending',
		);

		if (hasUserPublished && hasntCreatedPage) {
			complete('createPage');
		}
	}, [hasUserPublished, complete, actions]);

	const isFinished = actions.every((action) => action.state !== 'pending');
	const hasPoppedConfetti = useRef(false);

	useEffect(() => {
		if (onboardingStateLoading || !isFinished) {
			return;
		}

		const popConfettiIfVisible = () => {
			if (
				hasSeenConfetti ||
				hasPoppedConfetti.current ||
				document.visibilityState !== 'visible' ||
				createSpaceDrawerOpen
			) {
				return;
			}

			hasPoppedConfetti.current = true;
			void popOnboardingConfetti();
			void setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasSeenConfetti,
				value: 'true',
			});
		};

		// Check to see if the user is still viewing the page. The timeout is used
		// to ensure that the confetti doesn't pop if the user is navigating away.
		const timeout = setTimeout(popConfettiIfVisible, 50);

		document.addEventListener('visibilitychange', popConfettiIfVisible);

		return () => {
			// Cleanup timeout and event listener
			clearTimeout(timeout);
			document.removeEventListener('visibilitychange', popConfettiIfVisible);
		};
	}, [
		createSpaceDrawerOpen,
		onboardingStateLoading,
		hasSeenConfetti,
		isFinished,
		setOnboardingState,
	]);

	return {
		actions,
		dismiss,
		complete,
		errors,
		loading: onboardingStateLoading || premiumCardLoading,
		isFinished,
	};
}
